import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { StaffPayrollDto, PayrollStatus } from '../../services/payrollService';
import { payrollService } from '../../services/payrollService';

interface PayrollTableProps {
  data: StaffPayrollDto[];
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onViewPayroll: (payroll: StaffPayrollDto) => void;
  onApprovePayroll?: (payroll: StaffPayrollDto) => void;
  onProcessPayment?: (payroll: StaffPayrollDto) => void;
  onRejectPayroll?: (payroll: StaffPayrollDto) => void;
  onSubmitPayroll?: (payroll: StaffPayrollDto) => void;
  onDownloadPdf?: (payroll: StaffPayrollDto) => void;
  onSendEmail?: (payroll: StaffPayrollDto) => void;
  onDeletePayroll?: (payroll: StaffPayrollDto) => void;
  hasPermission: (role: string) => boolean;
}

const getStatusColor = (status: PayrollStatus): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case PayrollStatus.Draft:              return 'default';
    case PayrollStatus.PendingStaffApproval: return 'warning';
    case PayrollStatus.StaffApproved:     return 'info';
    case PayrollStatus.PendingAdminApproval: return 'warning';
    case PayrollStatus.AdminApproved:     return 'success';
    case PayrollStatus.PendingPayment:    return 'primary';
    case PayrollStatus.Paid:              return 'success';
    case PayrollStatus.Rejected:          return 'error';
    default:                              return 'default';
  }
};

export const PayrollTable: React.FC<PayrollTableProps> = ({
  data,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewPayroll,
  onApprovePayroll,
  onProcessPayment,
  onRejectPayroll,
  onSubmitPayroll,
  onDownloadPdf,
  onSendEmail,
  onDeletePayroll,
  hasPermission
}) => {
  const [deleteTarget, setDeleteTarget] = useState<StaffPayrollDto | null>(null);

  const confirmDelete = () => {
    if (deleteTarget && onDeletePayroll) {
      onDeletePayroll(deleteTarget);
    }
    setDeleteTarget(null);
  };
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>Period</TableCell>
            <TableCell>Hours</TableCell>
            <TableCell>Gross Pay</TableCell>
            <TableCell>Net Pay</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((payroll) => (
            <TableRow key={payroll.id}>
              <TableCell>
                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                  #{payroll.id}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {payroll.staffName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {payroll.employeeId}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {payrollService.formatDate(payroll.payrollStartDate)} - {payrollService.formatDate(payroll.payrollEndDate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {payroll.payrollFrequencyText}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">{Number(payroll.totalHours).toFixed(2)}h total</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {Number(payroll.regularHours).toFixed(2)}h regular + {Number(payroll.overtimeHours).toFixed(2)}h overtime
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold">
                  {payrollService.formatCurrency(payroll.grossPay)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {payrollService.formatCurrency(payroll.netPay)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={payroll.statusText || payrollService.getStatusText(payroll.status)}
                  color={getStatusColor(payroll.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => onViewPayroll(payroll)}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Download PDF — available for all statuses */}
                  {onDownloadPdf && (
                    <Tooltip title="Download PDF">
                      <IconButton size="small" color="secondary" onClick={() => onDownloadPdf(payroll)}>
                        <PdfIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Send Email */}
                  {onSendEmail && (
                    <Tooltip title="Send Email">
                      <IconButton size="small" color="info" onClick={() => onSendEmail(payroll)}>
                        <EmailIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Process Payment — available for all non-paid, non-rejected payrolls */}
                  {hasPermission('admin') && ![PayrollStatus.Paid, PayrollStatus.Rejected].includes(payroll.status) && onProcessPayment && (
                    <Tooltip title="Process Payment">
                      <IconButton size="small" color="primary" onClick={() => onProcessPayment(payroll)}>
                        <PaymentIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {/* Delete — available to admins for any status */}
                  {hasPermission('admin') && onDeletePayroll && (
                    <Tooltip title="Delete Payroll">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(payroll)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={data.length}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Payroll</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>Payroll #{deleteTarget?.id}</strong> for{' '}
            <strong>{deleteTarget?.staffName}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};
